import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { TopNavigationBar } from '../TopNavigation/index'
import {
  BlackBackground,
  LogoImage,
  WhiteBackground,
} from '../TopNavigation/components/CommonMenuItems'

import { SearchField } from '../../search/AllProductsSearch/components/SearchField'
import ContactUs from './contactUs/ContactUs'
import {
  ISelectedOption,
  LanguageDropdown,
} from '../../search/AllProductsSearch/LanguagesDropdown'
import { Translate2, Translate } from 'common'
import { StoreInstances } from '../../../store/StoreInstancesContainer'
import { defaultTheme } from '../../../theme/index'
import styled from 'styled-components'
import { HeaderProps } from './HeaderProps'
import AstPunchOutMenuItems from '../TopNavigation/components/MenuItemLinks/AstPunchOutMenuItems'
import {
  CatalogIndexTracker,
  CatalogLookupType,
} from '../../../api/metrics/CatalogIndexTracker'
import { SaytOption } from '../../../api/searchResults/interfaces'
import { isMobile } from 'react-device-detect'
import { useUserStore } from '../../../store/user/UserStore'
import { languageOptions } from 'common'
import AttributesServiceProvider from '../../../services/AttributesServiceProvider'
import AccountMenu from '../TopNavigation/components/accountMenu'
import { breakpoints } from '../../../theme/breakpoints'
import { getInterchangeParts } from '../../../helpers/getInterchangeParts'
import { NonCachedSearchType } from 'interfaces'

const isMultiPartInquiryEnabled =
  import.meta.env.VITE_FEATURE_MULTI_PART_INQUIRY_ENABLED === 'true'

const AstHeader = observer((props: HeaderProps): ReactElement => {
  const { logoUrl } = props
  const { searchStore } = StoreInstances
  const location = useLocation()

  const currentPathname = location.pathname
  const {
    addBreadCrumb,
    cleanBreadCrumbs,
    breadCrumbs,
    lastInterchangeTextSearched,
    lastNonCachedSearch,
  } = searchStore
  const navigate = useNavigate()
  const user = useUserStore()
  const handleAddBreadCrumb = (breadcrumb) => {
    if (breadCrumbs.length === 0) {
      addBreadCrumb(breadcrumb)
    } else {
      cleanBreadCrumbs()
      addBreadCrumb(breadcrumb)
    }
  }
  const [language, setLangauge] = useState<ISelectedOption>(
    languageOptions.find((item) => item.key === user.preferences.language) ||
      languageOptions[0]
  )

  const [interchangeTextFieldValue, setInterchangeTextFieldValue] = useState('')

  useEffect(() => {
    if (
      lastNonCachedSearch === NonCachedSearchType.searchByInterchange &&
      currentPathname === '/searchResults'
    ) {
      setInterchangeTextFieldValue(lastInterchangeTextSearched)
    } else {
      setInterchangeTextFieldValue('')
    }
  }, [lastInterchangeTextSearched, lastNonCachedSearch, currentPathname])

  const searchByText = (text: string): void => {
    searchStore.setShowPartsNotFitVehicleLabel(true)
    handleAddBreadCrumb({
      page: Translate2('allProducts'),
      breadcumb: text,
      url: () => searchStore.searchByText(text),
    })
    CatalogIndexTracker.setIndex(CatalogLookupType.AST_NO_MATCH)
    searchStore.searchByText(text)
    navigate('/searchResults')
  }

  const onSearch = (text: string): void => {
    searchByText(text)
  }

  const onSelectedOption = (saytOption: SaytOption): void => {
    searchByText(saytOption.label)
  }

  const onChangeLanguage = (selectedLanguage: ISelectedOption) => {
    const reqData = [
      {
        uuid: 'DEFAULT_LANGUAGE',
        domId: Number(user.preferences.userId),
        domType: 5,
        attrValue: selectedLanguage.key,
      },
    ]

    AttributesServiceProvider.saveOrUpdateAttribute(reqData)
    setLangauge(selectedLanguage)
  }

  return (
    <>
      <TopNavigationBar>
        <BlackBackground>
          {/* <MobileHamburguerMenu /> */}
          <NavLink to="/astPartsCatalog">
            <LogoImage src={logoUrl} alt="My place 4 parts logo" />
          </NavLink>
          <AstPunchOutMenuItems />
        </BlackBackground>
        <WhiteBackground>
          <AccountMenu />
        </WhiteBackground>
      </TopNavigationBar>

      <SubHeaderSection>
        <ASTSearchBox>
          <SearchField
            label={
              isMobile
                ? Translate('ast')
                : Translate('accessoriesSuppliesAndTools')
            }
            placeholder={Translate('searchPartBrandName')}
            onSearch={onSearch}
            onSelectOption={onSelectedOption}
            type="astOnly"
            multiPartInquiry={isMultiPartInquiryEnabled}
          />
        </ASTSearchBox>
        <OrDivider>{Translate('or')}</OrDivider>
        <SearchBoxWrapper>
          <SearchField
            label={Translate('interchange')}
            placeholder={Translate('separateMultiplePartNumbers')}
            onSearch={(text) => {
              getInterchangeParts(text)
            }}
            type="interchange"
            textFieldValue={interchangeTextFieldValue}
          />
        </SearchBoxWrapper>
        <RightWrapper>
          <ContactUs />
          <LangCont>
            <LanguageDropdown
              onLanguageChange={onChangeLanguage}
              selectedLanguage={language}
            />
          </LangCont>
        </RightWrapper>
      </SubHeaderSection>
    </>
  )
})

export default AstHeader

const SubHeaderSection = styled.div`
  width: 100%;
  height: 100px;
  box-shadow: 0 6px 6px -6px rgba(0, 0, 0, 0.3);
  background-color: ${defaultTheme.colors.white};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 42px;
  padding: ${isMobile ? '0 15px' : '0 0 0 15px'};
`

const LangCont = styled.div`
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    display: none;
  }
`

const RightWrapper = styled.div`
  display: flex;
  margin: 20px 0px 0px 0px;
  align-items: baseline;
  flex-wrap: wrap;
  margin-inline-start: auto;
  p {
    margin: 0px;
  }
`

const ASTSearchBox = styled.div`
  width: 519px;
`
const OrDivider = styled.span`
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 30px;
  margin-left: 10px;
  margin-right: 10px;

  @media (width > ${breakpoints.laptopXLarge}px) {
    margin-left: 24px;
    margin-right: 24px;
  }

  @media (width <= ${breakpoints.laptopXLarge}px) {
    margin-left: 16px;
    margin-right: 16px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`
const SearchBoxWrapper = styled.div`
  width: 100%;
  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 519px;
  }

  @media screen and (${breakpoints.laptopLarge}px < width <= ${breakpoints.laptopXLarge}px) {
    min-width: 395px;
    max-width: 519px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopLarge}px) {
    min-width: 395px;
    max-width: 395px;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 310px;
    max-width: 395px;
  }

  @media screen and (${breakpoints.tabletLarge}px <= width <= ${breakpoints.laptop}px) {
    min-width: 303px;
    max-width: 310px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    width: 100%;
  }
`
